import { useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import localforage from 'localforage';
import { useEmail } from '../../contexts/EmailContext';
import { useFileStore } from '../../stores/root_store';
import type { FileRecord } from '../../api/file_api';
import type { Email } from '../../api/email_api';

/** Info needed to reply to an incoming email message */
interface IncomingEmailReply {
  senderEmail: string;  // The email address to reply to
  subject?: string;     // Original subject
}

interface EmailComposerProps {
  open: boolean;
  onClose: () => void;
  requestUuid: string;
  blockedEmail?: string; // Email address that cannot be sent to (e.g., the client's email)
  replyToEmail?: Email; // Outgoing email to reply to (for threading)
  replyToIncomingEmail?: IncomingEmailReply; // Incoming email to reply to
}

interface DraftEmail {
  to: string;
  subject: string;
  content: string;
}

const DRAFT_KEY_PREFIX = 'EMAIL_DRAFT_';

export const EmailComposer = observer(function EmailComposer({
  open,
  onClose,
  requestUuid,
  blockedEmail,
  replyToEmail,
  replyToIncomingEmail,
}: EmailComposerProps) {
  const { sendEmail, sending, error } = useEmail();
  const fileStore = useFileStore();

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState<FileRecord[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);

  // Determine if we're in reply mode (either outgoing or incoming)
  const isReplyMode = !!(replyToEmail || replyToIncomingEmail);

  // Use different draft key for replies vs new emails
  const draftKey = replyToEmail
    ? `${DRAFT_KEY_PREFIX}reply_${replyToEmail.uuid}`
    : replyToIncomingEmail
    ? `${DRAFT_KEY_PREFIX}reply_incoming_${replyToIncomingEmail.senderEmail}`
    : `${DRAFT_KEY_PREFIX}${requestUuid}`;

  // Check if the current "to" email is blocked
  const isBlockedEmail = Boolean(blockedEmail && to.trim().toLowerCase() === blockedEmail.toLowerCase());

  // Load draft on mount, or pre-populate for reply mode
  useEffect(() => {
    if (open && requestUuid) {
      // Reply to outgoing email - pre-populate with reply data
      if (replyToEmail) {
        setTo(replyToEmail.to_email || '');
        // Add "Re:" prefix if not already present
        const existingSubject = replyToEmail.subject || '';
        const replySubject = existingSubject.toLowerCase().startsWith('re:')
          ? existingSubject
          : `Re: ${existingSubject}`;
        setSubject(replySubject);
        setContent('');
        return;
      }

      // Reply to incoming email - pre-populate with sender's email
      if (replyToIncomingEmail) {
        setTo(replyToIncomingEmail.senderEmail || '');
        // Add "Re:" prefix if not already present
        const existingSubject = replyToIncomingEmail.subject || '';
        const replySubject = existingSubject.toLowerCase().startsWith('re:')
          ? existingSubject
          : `Re: ${existingSubject}`;
        setSubject(replySubject);
        setContent('');
        return;
      }

      // New email mode - load from draft
      localforage.getItem<DraftEmail>(draftKey).then((draft) => {
        if (draft) {
          // Don't load the "to" if it matches the blocked email
          const draftTo = draft.to || '';
          const isBlocked = blockedEmail && draftTo.toLowerCase() === blockedEmail.toLowerCase();
          setTo(isBlocked ? '' : draftTo);
          setSubject(draft.subject || '');
          setContent(draft.content || '');
        } else {
          setTo('');
        }
      });
    }
  }, [open, requestUuid, draftKey, blockedEmail, replyToEmail, replyToIncomingEmail]);

  // Auto-save draft
  useEffect(() => {
    if (requestUuid && (to || subject || content)) {
      const draft: DraftEmail = { to, subject, content };
      localforage.setItem(draftKey, draft);
    }
  }, [to, subject, content, draftKey, requestUuid]);

  const handleSend = useCallback(async () => {
    setLocalError(null);

    if (!to.trim()) {
      setLocalError('Please enter a recipient email');
      return;
    }
    if (blockedEmail && to.trim().toLowerCase() === blockedEmail.toLowerCase()) {
      setLocalError('You cannot send emails to the client directly. Use the message system instead.');
      return;
    }
    if (!subject.trim()) {
      setLocalError('Please enter a subject');
      return;
    }
    if (!content.trim()) {
      setLocalError('Please enter message content');
      return;
    }

    try {
      const result = await sendEmail(
        'request',
        requestUuid,
        to.trim(),
        '', // from_email is now derived from request on backend
        subject.trim(),
        content.trim(),
        attachments,
        replyToEmail?.uuid // Pass reply-to email UUID for threading
      );

      if (result) {
        // Clear draft on success
        await localforage.removeItem(draftKey);
        setTo('');
        setSubject('');
        setContent('');
        setAttachments([]);
        onClose();
      }
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to send email');
    }
  }, [
    to,
    subject,
    content,
    attachments,
    requestUuid,
    blockedEmail,
    sendEmail,
    draftKey,
    onClose,
    replyToEmail,
  ]);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      const uploaded = await fileStore.uploadFiles(files);
      setAttachments((prev) => [...prev, ...uploaded]);

      e.target.value = '';
    },
    [fileStore]
  );

  const handleRemoveAttachment = useCallback((uuid: string) => {
    setAttachments((prev) => prev.filter((a) => a.uuid !== uuid));
  }, []);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
        },
      }}
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{isReplyMode ? 'Reply to Email' : 'Compose Email'}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Error */}
        {(localError || error) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {localError || error}
          </Alert>
        )}

        {/* Form */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            type="email"
            fullWidth
            size="small"
            disabled={sending}
            error={isBlockedEmail}
            helperText={isBlockedEmail ? "You cannot send emails to the client directly" : ""}
          />

          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            size="small"
            disabled={sending}
          />

          <TextField
            label="Message"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            rows={10}
            fullWidth
            disabled={sending}
            sx={{ flex: 1 }}
          />

          {/* Attachments */}
          {attachments.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Attachments
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {attachments.map((file) => (
                  <Chip
                    key={file.uuid}
                    label={file.filename}
                    size="small"
                    onDelete={() => handleRemoveAttachment(file.uuid)}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              disabled={sending}
            >
              Attach
              <input type="file" multiple hidden onChange={handleFileSelect} />
            </Button>

            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={handleSend}
              disabled={sending || !to.trim() || !subject.trim() || !content.trim() || isBlockedEmail}
              sx={{ flex: 1 }}
            >
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            Draft is auto-saved
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
});
